import { useState, useRef, useCallback, useEffect } from 'react';
import config from '../config';
import { authFetch, describeApiError } from '../api/authFetch';

export const useVoiceRecording = ({ showMessage, onTranscriptionComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState(null);
  const [recordingConfig, setRecordingConfig] = useState({
    language_code: 'en-US',
    enable_punctuation: true,
    quality_setting: 'standard',
    max_duration_seconds: 300,
    auto_transcribe: true, // Enable by default for better UX
    auto_send: true // New option to auto-send transcribed messages
  });

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Check if browser supports MediaRecorder
  const isSupported = useCallback(() => {
    return navigator.mediaDevices && 
           navigator.mediaDevices.getUserMedia && 
           window.MediaRecorder;
  }, []);

  // Initialize audio recording
  const initializeRecording = useCallback(async () => {
    if (!isSupported()) {
      throw new Error('Voice recording is not supported in this browser');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000, // Optimal for speech recognition
          channelCount: 1     // Mono audio
        }
      });

      streamRef.current = stream;

      // Determine the best audio format
      const options = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/wav';
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            delete options.mimeType; // Use default
          }
        }
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType || 'audio/wav' 
        });
        
        setAudioBlob(audioBlob);
        setAudioURL(URL.createObjectURL(audioBlob));

        // Stop the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Auto-transcribe if enabled
        if (recordingConfig.auto_transcribe) {
          await transcribeAudio(audioBlob);
        }
      };

      return mediaRecorder;

    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('Microphone access denied. Please allow microphone access and try again.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('No microphone found. Please check your audio devices.');
      } else {
        throw new Error('Something went wrong while starting the recording. Please try again.');
      }
    }
  }, [recordingConfig.auto_transcribe]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setTranscriptionResult(null);
      setAudioBlob(null);
      setAudioURL(null);
      setRecordingDuration(0);

      const mediaRecorder = await initializeRecording();
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          
          // Auto-stop at max duration
          if (newDuration >= recordingConfig.max_duration_seconds) {
            stopRecording();
            showMessage(`Recording stopped automatically after ${recordingConfig.max_duration_seconds} seconds`, 'info');
          }
          
          return newDuration;
        });
      }, 1000);

      showMessage('Recording started...', 'success');

    } catch (error) {
      console.error('Failed to start recording:', error);
      showMessage(describeApiError(error), 'error');
    }
  }, [initializeRecording, recordingConfig.max_duration_seconds, showMessage]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      // Pause timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      showMessage('Recording paused', 'info');
    }
  }, [isRecording, isPaused, showMessage]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          
          if (newDuration >= recordingConfig.max_duration_seconds) {
            stopRecording();
            showMessage(`Recording stopped automatically after ${recordingConfig.max_duration_seconds} seconds`, 'info');
          }
          
          return newDuration;
        });
      }, 1000);

      showMessage('Recording resumed', 'success');
    }
  }, [isRecording, isPaused, recordingConfig.max_duration_seconds, showMessage]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      showMessage('Recording completed', 'success');
    }
  }, [isRecording, showMessage]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    if (isRecording) {
      // Stop recording without processing
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }

      // Stop stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Reset state
      setIsRecording(false);
      setIsPaused(false);
      setAudioBlob(null);
      setAudioURL(null);
      setRecordingDuration(0);
      setTranscriptionResult(null);

      showMessage('Recording cancelled', 'info');
    }
  }, [isRecording, showMessage]);

  // Transcribe audio
  const transcribeAudio = useCallback(async (blob = audioBlob) => {
    if (!blob) {
      showMessage('No audio to transcribe', 'error');
      return;
    }

    setIsTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      formData.append('language_code', recordingConfig.language_code);
      formData.append('enable_punctuation', recordingConfig.enable_punctuation.toString());

      const response = await authFetch(`${config.API_BASE_URL}/api/audio/transcribe`, {
        method: 'POST',
        body: formData,
      }, { timeoutMs: 60000 });

      if (response.status === 401) {
        window.location.href = '/signin';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to transcribe audio');
      }

      const result = await response.json();

      if (result.success) {
        setTranscriptionResult(result);

        // Notify parent component
        if (onTranscriptionComplete) {
          onTranscriptionComplete(result);
        }

        showMessage('Audio transcribed successfully!', 'success');
      } else {
        throw new Error(result.error || 'Transcription failed');
      }

    } catch (error) {
      console.error('Transcription failed:', error);
      showMessage(`Transcription failed: ${describeApiError(error)}`, 'error');
    } finally {
      setIsTranscribing(false);
    }
  }, [audioBlob, recordingConfig, onTranscriptionComplete, showMessage]);

  // Upload audio file for transcription
  const uploadAudioFile = useCallback(async (file) => {
    setIsTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language_code', recordingConfig.language_code);
      formData.append('enable_punctuation', recordingConfig.enable_punctuation.toString());

      const response = await authFetch(`${config.API_BASE_URL}/api/audio/transcribe`, {
        method: 'POST',
        body: formData,
      }, { timeoutMs: 60000 });

      if (response.status === 401) {
        window.location.href = '/signin';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to transcribe audio');
      }

      const result = await response.json();

      if (result.success) {
        setTranscriptionResult(result);

        // Notify parent component
        if (onTranscriptionComplete) {
          onTranscriptionComplete(result);
        }

        showMessage('Audio transcribed successfully!', 'success');
        return result;
      } else {
        throw new Error(result.error || 'Transcription failed');
      }

    } catch (error) {
      console.error('Transcription failed:', error);
      showMessage(`Transcription failed: ${describeApiError(error)}`, 'error');
      throw error;
    } finally {
      setIsTranscribing(false);
    }
  }, [recordingConfig, onTranscriptionComplete, showMessage]);

  // Format duration for display
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, [audioURL]);

  return {
    // State
    isRecording,
    isPaused,
    audioBlob,
    audioURL,
    recordingDuration,
    isTranscribing,
    transcriptionResult,
    recordingConfig,
    
    // Actions
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    transcribeAudio,
    uploadAudioFile,
    setRecordingConfig,
    
    // Utilities
    isSupported,
    formatDuration
  };
};
